import React from 'react';
import {Helmet} from "react-helmet";
import { Box, Container, List, ListItem, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { getUTCTimeStamp } from '../utilities/helper';

const PrivacyPolicyPage: React.FC = () => {

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Scrap E-Auction Online Scrap Metal Auction Platform</title>
        <meta name="description" content="Privacy policy for Scrape E-Auction platform" />
      </Helmet>
      <Box sx={{ backgroundColor: '#fbf9fa'}}>
        <Container sx={{ py: 4 }}>
          <Stack gap={5} direction="column">
            <Box sx={{ mt: 4, textAlign: 'left' }}>
              <Typography variant="h1" gutterBottom sx={{ fontWeight: '600' }}>
              Privacy Policy
              </Typography>
              <Typography variant="body1" gutterBottom color="textSecondary">
                Last updated: {getUTCTimeStamp().format('DD/MM/YYYY')}
              </Typography>
            </Box>
            <Paper sx={{ p: 4 }}>
              <Stack gap={5} direction="column">
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >1. Information We Collect</Typography>
                  <Typography variant="body1" component="p">
                    We collect the following types of information:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary={<><Typography component="span" fontWeight="600">Personal Information: </Typography>Name, email, phone number, company details</>} />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary={<><Typography component="span" fontWeight="600">Business Information: </Typography>Company registration, PAN, GSTIN, address</>} />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary={<><Typography component="span" fontWeight="600">Transaction Data: </Typography>Bids, auction participation, documents uploaded</>} />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary={<><Typography component="span" fontWeight="600">Technical Data: </Typography>IP address, browser type, device information</>} />
                    </ListItem>
                  </List>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >2. How We Use Your Information</Typography>
                  <Typography variant="body1" component="p">
                  We use collected information for:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="User verification and account approval" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Facilitating auction participation and transactions" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Communicating important platform updates" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Improving platform security and performance" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Complying with legal obligations" />
                    </ListItem>
                  </List>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >3. Information Sharing</Typography>
                  <Typography variant="body1" component="p">
                    We may share your information:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="With other users as necessary for transaction completion (company name, contact details)" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="With service providers who assist in platform operations" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="With legal authorities when required by law" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="With your consent for specific purposes" />
                    </ListItem>
                  </List>
                  <Typography variant="body1" component="p">
                    We do not sell or rent your personal information to third parties.
                  </Typography>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >4. Data Security</Typography>
                  <Typography variant="body1" component="p">
                  We implement industry-standard security measures to protect your data, including:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Encrypted data transmission (HTTPS)" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Secure database storage" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Regular security audits" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Access controls and authentication" />
                    </ListItem>
                  </List>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >5. Cookies and Tracking</Typography>
                  <Typography variant="body1" component="p">
                  We use cookies and similar technologies to:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Maintain user sessions" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Remember user preferences" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Analyze platform usage" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Improve user experience" />
                    </ListItem>
                  </List>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >6. Your Rights</Typography>
                  <Typography variant="body1" component="p">
                  You have the right to:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Access your personal information" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Correct inaccurate data" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Request deletion of your account (subject to legal requirements)" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Opt-out of marketing communications" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Export your data" />
                    </ListItem>
                  </List>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >7. Data Retention</Typography>
                  <Typography variant="body1" component="p">
                  We retain your information for as long as your account is active and as required for:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Providing services to you" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Complying with legal obligations" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Resolving disputes" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Enforcing our agreements" />
                    </ListItem>
                  </List>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >8. Third-Party Links</Typography>
                  <Typography variant="body1" component="p">
                  Our platform may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. Please review their privacy policies.
                  </Typography>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >9. {`Children's Privacy`}</Typography>
                  <Typography variant="body1" component="p">
                  Our platform is intended for business use only. We do not knowingly collect information from individuals under 18 years of age.
                  </Typography>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >10. International Users</Typography>
                  <Typography variant="body1" component="p">
                  Your information may be transferred to and processed in servers located in India. By using our platform, you consent to this transfer.
                  </Typography>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >11. Changes to Privacy Policy</Typography>
                  <Typography variant="body1" component="p">
                  We may update this privacy policy from time to time. We will notify users of significant changes via email or platform notification.
                  </Typography>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >12. Contact Us</Typography>
                  <Typography variant="body1" component="p">
                  If you have questions about this privacy policy or how we handle your data, please contact us through the platform.
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default PrivacyPolicyPage;
